import * as THREE from 'three';

const FINDING_MESH_FILENAMES = Object.freeze({
  0: 'bin-maxillary-preop.vmd',
  1: 'bin-maxillary.vmd',
  2: 'bin-maxillary-scanbody.vmd',
  3: 'bin-mandibular-preop.vmd',
  4: 'bin-mandibular.vmd',
  5: 'bin-mandibular-scanbody.vmd',
  6: 'bin-bite.vmd',
  7: 'bin-bite-opposing.vmd',
});

const normalizeFilename = (filename = '') =>
  filename.replace(/\\/g, '/').split('/').pop().toLowerCase();

const MAXILLARY_MESH_TYPES = new Set([0, 1, 2]);
const MANDIBULAR_MESH_TYPES = new Set([3, 4, 5]);

export const getMeshDisplayRotationX = () => 0;

export const getMeshDisplayRotationY = (filename = '') => {
  const normalizedFilename = normalizeFilename(filename);
  const isMaxillary =
    normalizedFilename.includes('maxillary') || normalizedFilename.includes('upperjaw');
  return isMaxillary ? Math.PI : 0;
};

export const getMeshDisplayRotationZ = (filename = '') => {
  const normalizedFilename = normalizeFilename(filename);
  const isMandibular =
    normalizedFilename.includes('mandibular') ||
    normalizedFilename.includes('lowerjaw');
  return isMandibular ? Math.PI : 0;
};

export const getFindingDisplayRotationX = () => 0;

export const getFindingDisplayRotationY = (ownerMeshType) =>
  MAXILLARY_MESH_TYPES.has(Number(ownerMeshType)) ? Math.PI : 0;

export const getFindingDisplayRotationZ = (ownerMeshType) =>
  MANDIBULAR_MESH_TYPES.has(Number(ownerMeshType)) ? Math.PI : 0;

export const getOwnerMeshType = (filename = '') => {
  const normalizedFilename = normalizeFilename(filename);
  const isPreop = normalizedFilename.includes('preop');
  const isScanbody =
    normalizedFilename.includes('scanbody') || normalizedFilename.includes('marker');

  if (
    normalizedFilename.includes('mandibular') ||
    normalizedFilename.includes('lowerjaw')
  ) {
    if (isPreop) return 3;
    if (isScanbody) return 5;
    return 4;
  }

  if (
    normalizedFilename.includes('maxillary') ||
    normalizedFilename.includes('upperjaw')
  ) {
    if (isPreop) return 0;
    if (isScanbody) return 2;
    return 1;
  }

  return null;
};

const isMatrix = (values) =>
  Array.isArray(values) &&
  values.length === 16 &&
  values.every((value) => Number.isFinite(Number(value)));

export const isTransformFile = (filename = '') =>
  normalizeFilename(filename) === 'transform.json';

export const normalizeMeshTransforms = (payload) => {
  const entries = Array.isArray(payload) ? payload : payload?.transforms;
  if (!Array.isArray(entries)) return {};

  return entries.reduce((transforms, entry) => {
    if (entry?.file && isMatrix(entry.mat)) {
      transforms[normalizeFilename(entry.file)] = entry.mat.map(Number);
    }
    return transforms;
  }, {});
};

export const decodeMeshTransforms = (buffer) => {
  const json = new TextDecoder('utf-8').decode(buffer).replace(/^\uFEFF/, '');
  return normalizeMeshTransforms(JSON.parse(json));
};

export const getMeshTransform = (transforms = {}, filename = '') =>
  transforms[normalizeFilename(filename)] || null;

export const getFindingTransform = (transforms, ownerMeshType) => {
  const filename = FINDING_MESH_FILENAMES[Number(ownerMeshType)];
  return filename ? getMeshTransform(transforms, filename) : null;
};

const createTransformMatrix = (values) =>
  isMatrix(values) ? new THREE.Matrix4().set(...values.map(Number)) : null;

export const applyMeshTransform = (geometry, transforms, filename) => {
  if (normalizeFilename(filename).endsWith('.ply')) return;

  const matrix = createTransformMatrix(getMeshTransform(transforms, filename));
  if (matrix) geometry.applyMatrix4(matrix);
};

export const transformPosition = (position, values) => {
  const matrix = createTransformMatrix(values);
  if (!matrix) return position.map(Number);
  return new THREE.Vector3(...position.map(Number)).applyMatrix4(matrix).toArray();
};
